import { ComboboxData, ComboboxItem } from "@mantine/core";
import { DeepRequired } from "@mctypes/global.types";
import { IIssueBoardApi } from "@schemas/issueBoardSchema";
import { IIssueApi, IIssueUpdateSchemaProps, IIssueUpdateUserSchemaProps, IUserPartial } from "@schemas/issueSchema";
import {
  PutProjectByProjectIdIssueUpdateSimpleApiArg,
  PutProjectByProjectIdIssueUpdateUserApiArg,
} from "../../../../../../../openapi";
import { IssueDisabledColumnsEnum } from "../../../../../../mc-shared/enums";

export type CellCtx = {
  issue: IIssueApi;
  issueBoard: IIssueBoardApi;
  projectId: string;

  usersComboboxData: ComboboxItem[];
  multiUsersComboboxData: IUserPartial[];
  bucketOptions: ComboboxData;
  typeOptions: ComboboxData;
  typeAheadPNSItems: string[];
  issueTags: string[];
  tagsLoading: boolean;

  updateSimple: (prop: IIssueUpdateSchemaProps) => Promise<void>;
  updateUser: (prop: IIssueUpdateUserSchemaProps) => Promise<void>;
};

export type CellDef = {
  /** Unique key for React list + ordering */
  id: string;
  /** If set, this cell can be hidden via IssueDisabledColumnsEnum[] */
  disableKey?: IssueDisabledColumnsEnum;

  span: number | ((ctx: CellCtx) => number);
  when?: (ctx: CellCtx) => boolean;
  render: (ctx: CellCtx) => React.ReactNode;
};

export async function guardedIssueUpdate<TProp extends Record<string, unknown>, TArg>({
  issue,
  prop,
  mutation,
  buildPayload,
}: {
  issue: IIssueApi;
  prop: TProp;
  mutation: (arg: TArg) => unknown;
  buildPayload: (prop: TProp) => TArg;
}) {
  const key = Object.keys(prop)[0] as keyof IIssueApi;
  const newValue = prop[key];
  const oldValue = issue[key];

  const unchanged = JSON.stringify(oldValue) === JSON.stringify(newValue);

  if (unchanged) {
    return;
  }

  await mutation(buildPayload(prop));
}

export const TASK_TYPES: ComboboxData = [
  { label: "Opprettet", value: "Opprettet" },
  { label: "I prosess", value: "I prosess" },
  { label: "På hold", value: "På hold" },
  { label: "Fullført", value: "Fullført" },
  { label: "Utgår", value: "Utgår" },
];

export const PRIORITY_OPTIONS: ComboboxData = [
  { value: "HIGH", label: "Høy" },
  { value: "MEDIUM", label: "Middels" },
  { value: "LOW", label: "Lav" },
];

export const createSimplePayload =
  (projectId: string, issueId: string) =>
  (prop: IIssueUpdateSchemaProps): PutProjectByProjectIdIssueUpdateSimpleApiArg => ({
    projectId,
    body: {
      _id: issueId,
      property: prop as PutProjectByProjectIdIssueUpdateSimpleApiArg["body"]["property"],
    },
  });

export const createUserPayload =
  (projectId: string, issueId: string) =>
  (prop: DeepRequired<IIssueUpdateUserSchemaProps>): PutProjectByProjectIdIssueUpdateUserApiArg => ({
    projectId,
    body: {
      _id: issueId,
      property: prop,
    },
  });
